import React from "react";
import {
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiImage,
  FiList,
  FiStar,
  FiToggleLeft,
  FiToggleRight,
  FiTrash2,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaAlbumServices";

import AdminButton from "./../../_shared/ui/AdminButton";

import {
  formatShortDate,
  getAlbumStatusClassName,
  getAlbumStatusLabel,
  truncateText,
} from "../utils/mediaAlbumsAdminUtils";

export default function MediaAlbumCard({
  item,
  actionId,
  onEdit,
  onToggleActive,
  onTogglePublic,
  onToggleHome,
  onManagePhotos,
  onAskDelete,
}) {
  const coverUrl = item?.cover_url
    ? item.cover_url
    : toAbsolutePublicUrl(item?.cover_media?.url);

  const activeLoading = actionId === `active-${item.id}`;
  const publicLoading = actionId === `public-${item.id}`;
  const homeLoading = actionId === `home-${item.id}`;
  const photosLoading = actionId === `photos-${item.id}`;

  return (
    <article className="group overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30 dark:hover:bg-slate-900 dark:hover:shadow-black/20">
      <div className="grid min-h-full sm:grid-cols-[210px_1fr]">
        <div className="relative min-h-[210px] overflow-hidden bg-slate-200 dark:bg-slate-800">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={item.title || "Couverture de l’album"}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full min-h-[210px] flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 dark:from-slate-800 dark:to-slate-900 dark:text-slate-500">
              <FiImage className="text-4xl" />
              <span className="text-xs font-bold uppercase tracking-[0.18em]">
                Sans couverture
              </span>
            </div>
          )}

          <div className="absolute left-3 top-3">
            <span
              className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${getAlbumStatusClassName(
                item,
              )}`}
            >
              {getAlbumStatusLabel(item)}
            </span>
          </div>

          {item.show_on_home ? (
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full border border-white/30 bg-sky-600/95 px-3 py-1 text-xs font-bold text-white shadow-lg backdrop-blur">
              <FiStar />
              Accueil
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col p-4 sm:p-5">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              {formatShortDate(item.created_at)} · ordre {item.display_order}
            </p>

            <h3 className="mt-2 line-clamp-2 text-lg font-bold leading-snug text-slate-950 dark:text-white">
              {item.title || "Album sans titre"}
            </h3>

            <p className="mt-2 line-clamp-1 text-xs font-bold text-slate-400 dark:text-slate-500">
              /{item.slug || "slug-non-defini"}
            </p>

            <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {truncateText(item.description, 210) ||
                "Aucune description renseignée."}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
                <FiImage />
                {Number(item.total_items || item.items?.length || 0)} photo(s)
              </span>

              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
                {item.is_public ? <FiEye /> : <FiEyeOff />}
                {item.is_public ? "Public" : "Privé"}
              </span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <AdminButton
              icon={FiList}
              variant="primary"
              size="sm"
              loading={photosLoading}
              onClick={() => onManagePhotos(item.id)}
            >
              Photos
            </AdminButton>

            <AdminButton
              icon={FiEdit2}
              variant="soft"
              size="sm"
              onClick={() => onEdit(item.id)}
            >
              Modifier
            </AdminButton>

            <AdminButton
              icon={item.is_active ? FiToggleLeft : FiToggleRight}
              variant={item.is_active ? "secondary" : "primary"}
              size="sm"
              loading={activeLoading}
              onClick={() => onToggleActive(item)}
            >
              {item.is_active ? "Masquer" : "Activer"}
            </AdminButton>

            <AdminButton
              icon={item.is_public ? FiEyeOff : FiEye}
              variant="secondary"
              size="sm"
              loading={publicLoading}
              onClick={() => onTogglePublic(item)}
            >
              {item.is_public ? "Privé" : "Public"}
            </AdminButton>

            <AdminButton
              icon={FiStar}
              variant={item.show_on_home ? "secondary" : "soft"}
              size="sm"
              loading={homeLoading}
              onClick={() => onToggleHome(item)}
            >
              {item.show_on_home ? "Retirer accueil" : "Accueil"}
            </AdminButton>

            <AdminButton
              icon={FiTrash2}
              variant="dangerSoft"
              size="sm"
              onClick={() => onAskDelete(item.id)}
            >
              Supprimer
            </AdminButton>
          </div>
        </div>
      </div>
    </article>
  );
}